/**
 * Actors: projectiles, enemies, loot and stations (PRD §12, §16, §17, §19, §20).
 *
 * Deliberately not an ECS (PRD §33) — plain pooled arrays and a small enum
 * state machine per enemy is all this needs.
 *
 * This module owns the pools, the collision resolution, loot and the dispatch;
 * the per-species steering lives in `behaviors.ts` so that adding a creature
 * does not mean editing the same 900 lines everything else lives in.
 */

import { Rng } from '../core/noise';
import { angleDelta, clamp, damp, hyp } from '../core/math';
import { carve, density, featuresFor, floorAt, gradient, isClear } from './field';
import { LAYERS, laneCeiling, layerLift, toField, type LayerSpec } from './mesher';
import {
  CARGO, DROPS, ENEMY, FISH_MIX, LOOT, MINE, PICKUP_DEF, SONAR, WEAPONS, WORLD_SEED,
  type DropChance, type EnemyType, type PickupType,
} from './config';
import { POCKETS, pocketAt, profileY, regionAt } from './level';
import { CAMPAIGN_CONTENT, type ModeContent } from './mode';
import * as AI from './behaviors';

/** Scratch for lane-aware projectile terrain tests. Allocates nothing per shot. */
const projField = { x: 0, y: 0 };
/** Scratch for the ricochet normal. Same reason `projField` is module-level. */
const projNormal = { x: 0, y: 0 };
/** The same, for loot and cargo. Separate so the two cannot tread on each other. */
const pickField = { x: 0, y: 0 };

/**
 * What a kill inside a pocket pays, against the same kill on the main lane.
 *
 * Above one, and deliberately. The main lane carries the economy; a pocket is
 * a room you had to find a gate for, cross, and get back out of, and it is
 * meant to be worth that. Blanketing the far lanes end to end and then paying
 * LESS per kill was the previous shape and it was wrong twice over — it made
 * the world three times bigger and every fight in it worth less.
 */
const POCKET_SALVAGE = 1.6;

/** Horizontal offset between a lane's drawn x and its field x. */
const LANE_WARP = (lane: number) =>
  LAYERS[Math.max(0, Math.min(LAYERS.length - 1, lane))]!.warp;

export type Faction = 'player' | 'enemy';

/** Anything a torpedo can chase — enemies and the boss both satisfy this. */
export interface Trackable {
  x: number;
  y: number;
  readonly alive: boolean;
}

export type ProjectileKind = 'bullet' | 'torpedo' | 'charge';

export interface Projectile {
  alive: boolean;
  kind: ProjectileKind;
  from: Faction;
  x: number; y: number; vx: number; vy: number;
  /**
   * DRAWING ONLY. Never read by collision, steering or the blast solver, all of
   * which are strictly two-dimensional and must stay that way.
   *
   * It exists for one reason: a torpedo launched from a flank tube starts level
   * with the middle of the boat, and at z 0 — the centreline — it is inside the
   * hull volume and the depth test hides it until it clears the bow. Half a
   * metre outboard puts it beside the plating instead of within it, which is
   * where a tube on the side of a submarine would actually put it.
   */
  z: number;
  /**
   * Parallax lane this shot lives in, or -1 in the water between lanes.
   *
   * Collision stays two-dimensional exactly as before; the lane only chooses
   * WHICH two dimensions, by picking the field the shot is tested against. A
   * torpedo fired in the middle lane must hit the rock drawn around it, not the
   * rock 431.7 m away that happens to share its screen position.
   */
  lane: number;
  life: number;
  damage: number;
  radius: number;
  blast: number;
  angle: number;
  target: Trackable | null;
  trail: number;
  /** Metres of rock this round carves when it goes off. 0 = leaves no mark. */
  carve: number;
  /**
   * Ricochets left in this round.
   *
   * Only cannon rounds carry any. A torpedo that bounced off a wall would be a
   * torpedo that failed, and a depth charge is meant to settle.
   */
  bounces: number;
  /** Steering authority for guided rounds. */
  turnRate: number;
  maxSpeed: number;
  accel: number;
}

export type EnemyState =
  | 'idle' | 'suspicious' | 'investigate' | 'attack' | 'search'
  | 'rush' | 'recover' | 'flee' | 'burst';

export interface Enemy {
  alive: boolean;
  type: EnemyType;
  x: number; y: number; vx: number; vy: number;
  /**
   * Parallax lane this one occupies. Everything authored is in lane 0, and
   * steering, line of sight and separation are all still solved on the
   * unwarped field, so this is not yet a creature that can BE somewhere else —
   * it is what stops a shot fired three lanes away from killing it through
   * 431.7 m of rock because the two happen to share a screen position.
   */
  lane: number;
  homeX: number; homeY: number;
  hp: number; maxHp: number;
  state: EnemyState;
  stateTime: number;
  alert: number;
  cooldown: number;
  facing: number;
  spin: number;
  phase: number;
  hitFlash: number;
  radius: number;
  score: number;
  salvage: number;
  /** Last position the enemy believes the player occupies. */
  lastSeenX: number; lastSeenY: number;
  /** Painted by an active sonar ping (PRD §14). */
  pinged: number;
  /** Which generated mesh variant to draw (jellyfish species, hull marks). */
  variant: number;
  /** Rendered scale for organics. */
  size: number;
  /** Animation rate, fixed at spawn. */
  rate: number;

  // --- extras the newer species need ---------------------------------------
  /** General-purpose per-behaviour accumulator (burst counters, lunge timers). */
  timer: number;
  timer2: number;
  /** Where a barrel or an armour plate is currently pointing, radians. */
  aim: number;
  /** Surface normal an anchored enemy is bolted to. */
  mountX: number; mountY: number;
  /** Bolted to or moored in rock on purpose. Exempt from the push-out. */
  anchored: boolean;
  /** Shoal id — swarmers sharing one flock together. */
  group: number;
  /** Destructible sub-parts. Used by the capital sub's weapon pods. */
  podA: number; podB: number;
  podMax: number;
  /** 0..1 telegraph ramp the renderer reads for lures, charges and glows. */
  charge: number;
  /** Set for anything the world treats as a zone boss. */
  boss: boolean;
  /**
   * This one has already found you.
   *
   * Every hunter in the game gates on a "did I notice the sub" test — sight
   * range, hearing, line of sight — and that gate is what makes the campaign a
   * stalking game: things sit in the dark until you give yourself away. A horde
   * is the opposite proposition. It knows where you are and it is coming, and
   * the only question is whether you can kill it fast enough.
   *
   * So this does not add a behaviour. It holds the existing perception gate
   * open, and every species then advances using its own attack pattern — drones
   * in formation, seekers on a straight run, hunters flanking. Same creatures,
   * different rules, which is the whole point of a mode.
   */
  /**
   * Stable identity for an AUTHORED enemy: its zone index and its index within
   * that zone. `spawnLevel` walks `content.zones` with a fixed seed, so the same
   * pair always produces the same creature in the same place — which is what
   * lets a save record that this one is already dead. Empty for anything spawned
   * at runtime, which is nothing a save has to remember.
   */
  /**
   * Seconds until this one goes off, for a mine or seeker caught in someone
   * else's blast. 0 means no fuse.
   */
  fuse: number;
  zoneKey: string;
  march: boolean;
  /**
   * Carrying something. A carrier is worth killing first: it has many times the
   * hit points of its species, it shows those hit points as a number over the
   * hull so the choice to focus it is informed rather than hopeful, and it drops
   * what it is carrying when it dies.
   */
  carrier: PickupType | null;
  /** Boss id, so a defeated boss stays defeated across a reload. */
  id: string;
}

/** `towed` is owned by the tow solver; `updatePickups` leaves it alone. */
export type HoldState = 'free' | 'towed';

export interface Pickup {
  alive: boolean;
  type: PickupType;
  /**
   * Parallax lane this sits in. Everything authored is 0.
   *
   * Loot has to know: it falls until it meets the seabed, and a lane's drawn
   * seabed is `yOffset` above the near one — 54 m on lane 2. Dropped without a
   * lane it sinks straight through the ground it is drawn on and settles on
   * rock the player cannot reach.
   */
  lane: number;
  x: number; y: number;
  vx: number; vy: number;
  amount: number;
  label: string;
  phase: number;
  /**
   * Tonnes. 0 is a pickup in the old sense — it is pocketed by flying into it.
   * Anything above 0 is cargo: it has weight, it sinks, it settles on the
   * seabed, it cannot be collected by touch, and the only way it goes anywhere
   * is on the end of a line.
   */
  mass: number;
  hold: HoldState;
  /**
   * WHICH SEAM, for an `ore` shard. Undefined on everything else.
   *
   * On the pickup rather than in the type for the reason `PickupType` gives:
   * eight more types would mean eight more entries in every drop table and
   * every switch, for a distinction that is one index into `ORE`.
   */
  ore?: number;
  /**
   * Where this was authored. The save records only crates that have actually
   * moved, and "moved" has to be answerable without consulting level.ts from
   * inside World.
   */
  homeX: number;
  homeY: number;
  /** Some rewards only exist once sonar has found them (PRD §19). */
  revealed: boolean;
  /** Authored pickups carry an id so a save knows they are gone. Loot has ''. */
  id: string;
  /** Seconds before loot fades. Infinity for authored pickups. */
  life: number;
  /** 0 → 1 over the first moments, for the spawn pop. */
  born: number;
}

/** Repair-and-save station (PRD §20). Permanent, re-usable, never consumed. */
export interface Station {
  id: string;
  /** Stations are installations in the near lane; nothing authors one elsewhere. */
  lane: number;
  x: number; y: number;
  name: string;
  active: boolean;
  phase: number;
  /** 0..1 proximity, for the glow and the dock prompt. */
  near: number;
}

export interface Blast {
  x: number; y: number;
  /** Lane the explosion happened in. Nothing outside it is touched. */
  lane: number;
  radius: number;
  damage: number;
  from: Faction;
  scale: number;
}

/**
 * Everything the actors need to know about the player and the effect sinks, so
 * neither this module nor `behaviors.ts` ever reaches back into World.
 */
export interface ActorCtx {
  px: number; py: number; pvx: number; pvy: number;
  playerAlive: boolean;
  hasLineOfSight: (x: number, y: number) => boolean;
  lightOn: boolean;
  lightAngle: number;
  noisy: boolean;
  sonarPing: { x: number; y: number; r: number } | null;
  playerRadius: number;
  /** Which lane the sub is in, so a shot cannot hit across lanes. */
  playerLane: number;
  /** A live handle on the player, so enemy torpedoes can home. */
  playerTrack: Trackable;
  /** True while the sub is under nitro fast enough to ram through things. */
  ramming: boolean;
  /** Continuous damage — no invulnerability frames. */
  grazePlayer: (amount: number) => void;
  /** A discrete hit — buys the player a slice of invulnerability. */
  hitPlayer: (amount: number) => void;
  /** Explosion damage, which the ward field resists. */
  blastPlayer: (amount: number) => void;
  onExplosion: (x: number, y: number, scale: number) => void;
  onBubbleTrail: (x: number, y: number) => void;
  onSpark: (x: number, y: number, nx: number, ny: number, n: number) => void;
  /** A jelly or a bloom coming apart, in its own colour. */
  onBioBurst: (x: number, y: number, scale: number, r: number, g: number, b: number) => void;
  onPickup: (p: Pickup) => void;
  onScore: (n: number) => void;
  onKill: (e: Enemy) => void;
  onShake: (n: number) => void;
  /** Rock was removed; the terrain needs to re-mesh this span. */
  onCarve: (x0: number, x1: number) => void;
  onEnemyShot: (x: number, y: number, kind: 'gun' | 'turret' | 'torpedo' | 'ping' | 'bite') => void;
}

const rngOf = (salt: number) => new Rng(WORLD_SEED ^ salt);

/** Scratch normal for the terrain push-out, so the hot loop allocates nothing. */
const push = { x: 0, y: 1 };

export class Actors {
  projectiles: Projectile[] = [];
  enemies: Enemy[] = [];
  /**
   * Multiplier on the loot magnet's reach, set by the mode.
   *
   * 26 m is right for the campaign, where the fight is over before you collect.
   * In a tide the loot drops inside the front, and walking in to fetch a shield
   * cell you need *because* you are being overwhelmed is exactly backwards.
   * Survival turns this up so a drop comes to you.
   */
  magnetScale = 1;
  /**
   * Rolled on every kill on top of the species table, and salvage suppressed
   * with it. Survival has no currency — `purse` routes into a pot that declines
   * — so a swarmer dropping fourteen salvage is litter, while the thing the
   * player actually needs mid-tide is a shield cell. The mode says what a kill
   * is worth; the species table stays the campaign's.
   */
  extraDrops: DropChance[] = [];
  suppressSalvage = false;
  pickups: Pickup[] = [];
  stations: Station[] = [];
  blasts: Blast[] = [];
  /** Enemy count killed, for scoring/telemetry. */
  kills = 0;
  /** Shared clock, so per-creature animation never depends on a mutating field. */
  time = 0;
  /**
   * Shoal membership, built once. A swarmer's flock rule only cares about its
   * own group and a group never changes, so scanning the whole enemy list per
   * fish per frame was O(n squared) for no reason — and the reason it mattered
   * is that the shoals got three times bigger.
   */
  readonly swarmGroups = new Map<number, Enemy[]>();

  /**
   * What this world is built from. Defaulted, and that default is load-bearing:
   * `new Actors()` is called from `scripts/audit-world.mjs` twice and from
   * `scripts/simulate.mjs`, and a required parameter would break `npm run
   * verify` on the first commit of this refactor for no benefit.
   */
  readonly content: ModeContent;

  constructor(content: ModeContent = CAMPAIGN_CONTENT) {
    this.content = content;
    for (let i = 0; i < 320; i++) {
      this.projectiles.push({
        alive: false, kind: 'bullet', from: 'player',
        x: 0, y: 0, z: 0, lane: 0, vx: 0, vy: 0, life: 0, damage: 0, radius: 0, blast: 0,
        angle: 0, target: null, trail: 0, carve: 0, bounces: 0,
        turnRate: 0, maxSpeed: 0, accel: 0,
      });
    }
    this.spawnLevel();
  }

  // ==========================================================================
  // spawning
  // ==========================================================================

  private blank(type: EnemyType, x: number, y: number, rng: Rng): Enemy {
    const s = ENEMY[type];
    return {
      alive: true,
      type,
      lane: 0,
      anchored: false,
      x, y, vx: 0, vy: 0,
      homeX: x, homeY: y,
      hp: s.hp, maxHp: s.hp,
      state: 'idle', stateTime: 0, alert: 0, cooldown: 0,
      facing: rng.next() < 0.5 ? -1 : 1,
      spin: rng.range(0, 6.28),
      phase: rng.range(0, 6.28),
      hitFlash: 0,
      radius: s.radius,
      score: s.score,
      salvage: s.salvage,
      lastSeenX: x, lastSeenY: y,
      pinged: 0,
      variant: 0,
      size: 1,
      rate: 1,
      timer: 0, timer2: 0,
      aim: 0,
      mountX: 0, mountY: 1,
      group: 0,
      podA: 0, podB: 0, podMax: 0,
      fuse: 0, zoneKey: '', march: false, carrier: null,
      charge: 0,
      boss: false,
      id: '',
    };
  }

  private spawnLevel() {
    const rng = rngOf(0x1f00d);

    /**
     * The authored zone table, planted in every lane.
     *
     * A lane is a real place — layer 1 is the seabed 431.7 m along — so the
     * honest population for it is the same table read at that offset, which is
     * what `floorAt(x + warp) + layerLift` gives. Lane 0 is generated first and
     * bit-for-bit as before: its warp and lift are zero, and its ids and zone
     * keys are the ones a save already holds. The far lanes deliberately get no
     * ids and no bosses — a boss is a landmark a save tracks by identity, and
     * two of the same one is not a fight, it is a bookkeeping error.
     */
    for (let lane = 0; lane < LAYERS.length; lane++) {
      const L = LAYERS[lane]!;
      const far = lane > 0;
      for (let zi = 0; zi < this.content.zones.length; zi++) {
        const z = this.content.zones[zi];
        if (far && (z.boss || z.id)) continue;
        for (let i = 0; i < z.count; i++) {
          const spread = z.count > 1 ? (i + 0.5) / z.count : 0.5;
          const x = z.x0 + spread * (z.x1 - z.x0) + rng.range(-9, 9);
          const lift = z.lift ?? [10, 30];
          // Seated on the ground of its own lane, in drawn coordinates.
          const base = z.from === 'absolute' ? 0
            : z.from === 'profile' ? profileY(x + L.warp) + layerLift(x, L)
            : floorAt(x + L.warp) + layerLift(x, L);
          const y = base + rng.range(lift[0], lift[1]);
          // A far lane exists only where a pocket cuts into it. Everywhere else
          // it stays what it always was: a silhouette, with nothing in it.
          const pocket = far ? pocketAt(lane, x) : null;
          if (far && !pocket) continue;
          const e = this.spawnEnemy(z.type, x, y, rng, {
            tough: (z.tough ?? 1) * (pocket ? pocket.tough : 1),
            group: z.group, id: far ? undefined : z.id,
            boss: far ? false : z.boss, anchor: z.anchor === true,
            // Groups are per-lane, or two flocks in different places share an
            // id and mill about steering at each other through rock.
            lane, ...(z.group ? { group: z.group + lane * 1000 } : {}),
          });
          if (e) {
            e.zoneKey = far ? '' : `${zi}:${i}`;
            if (pocket) e.salvage = Math.round(e.salvage * POCKET_SALVAGE);
          }
        }
      }
    }

    // The cache, at the FAR end of each room from its gate, so the room has to
    // be crossed rather than dipped into. Authored ids, so a save remembers a
    // room already emptied — these are the reward for finding the place, and
    // finding it twice should not pay twice.
    // Only for a mode that builds the world. `ambient` is the same marker the
    // jellies use, and a mode that declines everything must produce NOTHING —
    // `npm run mode-parity` exists to catch exactly this, and did.
    // AUTHORED, not spawned. `spawnLoot` takes its phase from `Math.random()`,
    // which is right for loot bursting out of a kill and wrong for a thing that
    // is part of the world: it made the level itself differ between two builds
    // of the same seed. `mode-parity` caught it, which is its whole job.
    const crng = rngOf(0x0cac8e);
    for (const q of this.content.ambient ? POCKETS : []) {
      const L = LAYERS[Math.min(q.lane, LAYERS.length - 1)]!;
      const far = q.gateX - q.x0 > q.x1 - q.gateX ? q.x0 + 30 : q.x1 - 30;
      const y = floorAt(far + L.warp) + layerLift(far, L) + 7;
      this.pickups.push({
        alive: true, type: 'salvage', lane: q.lane,
        x: far, y, vx: 0, vy: 0,
        amount: q.reward, label: q.name,
        phase: crng.range(0, 6.28),
        mass: 0, hold: 'free',
        homeX: far, homeY: y,
        revealed: true, id: `cache-${q.id}`,
        life: Infinity, born: 1,
      });
    }

    this.spawnJellies(rng);
    this.spawnBlooms(rng);
    // Swarm membership is registered by `spawnEnemy` itself, so that a runtime
    // spawn joins its flock the same way an authored one does. There used to be
    // a second pass over `enemies` here doing the same job, which after the
    // extraction put every fish in its group twice.
    for (const spec of this.content.pickups) {
      // Authored positions are hints; make sure nothing ends up inside rock.
      let py = spec.y;
      const pf = featuresFor(spec.x - 4, spec.x + 4);
      let guard = 0;
      while (density(spec.x, py, pf) > -2.5 && guard++ < 30) py += 1.5;
      const def = PICKUP_DEF[spec.type];
      this.pickups.push({
        alive: true,
        type: spec.type,
        lane: 0,
        x: spec.x, y: py,
        vx: 0, vy: 0,
        amount: spec.amount ?? def.amount,
        label: spec.label ?? def.label,
        phase: rng.range(0, 6.28),
        mass: spec.mass ?? 0,
        hold: 'free',
        homeX: spec.x, homeY: py,
        revealed: !spec.hidden,
        id: spec.id,
        life: Infinity,
        born: 1,
      });
    }

    for (const s of this.content.stations) {
      let sy = s.y;
      const sf = featuresFor(s.x - 5, s.x + 5);
      let guard = 0;
      while (density(s.x, sy, sf) > -4 && guard++ < 40) sy += 2;
      this.stations.push({
        id: s.id, lane: 0, x: s.x, y: sy, name: s.name,
        active: false, phase: rng.range(0, 6.28), near: 0,
      });
    }
  }

  /** Per-species spawn dressing: size, animation rate, mounts, aim. */
  private configure(e: Enemy, rng: Rng, anchor: boolean) {
    switch (e.type) {
      case 'swarmer':
        e.size = rng.range(0.62, 1.2);
        e.rate = rng.range(2.4, 3.6);
        // Species is per fish, not per shoal — a mixed school looks alive in a
        // way a uniform one never does — but weighted, so most of it is the
        // sleek silhouette and the odd shapes stay occasional.
        e.variant = FISH_MIX[rng.int(0, FISH_MIX.length - 1)];
        e.aim = e.facing > 0 ? 0 : Math.PI;
        // `spin` is seeded with a random angle for the things that use it as a
        // rotation. For a fish it is the bank, and it drives body curvature, so
        // a fish that has not moved yet would start folded in half.
        e.spin = 0;
        break;
      case 'lurker':
        e.size = rng.range(1.0, 1.5);
        e.rate = rng.range(0.7, 1.1);
        break;
      // THE AUTHORED BLOOMS WERE NEVER DRESSED.
      //
      // `jellyDrift` and `bloomDrift` size their own animals; the twenty hand
      // placed bands in level.ts go through `configure` instead, and it had no
      // case for either. So every jellyfish the level deliberately put where
      // the player would see it came out of `blank` at the defaults: size 1,
      // rate 1, variant 0. One species, one size, one tempo — a whole bloom of
      // identical runts pulsing in lockstep, and it is the bulk of the
      // population, which is why the mean size in the play lane measured 1.12
      // against 2.5 for a decorative prop and 2.7 for a background one.
      //
      // The animals nearest the player were the smallest things on screen. Same
      // ranges the drift spawners use, so the two populations agree.
      case 'jelly':
        e.size = rng.range(0.9, 2.4);
        e.rate = rng.range(1.0, 1.6);
        e.variant = rng.int(0, 4);
        break;
      case 'bloom':
        e.size = rng.range(3.2, 6.4);
        e.rate = rng.range(0.75, 1.15);
        e.variant = rng.int(0, 2);
        break;
      case 'turret':
        e.aim = Math.PI;
        break;
      case 'sentinel':
        e.aim = e.facing > 0 ? 0 : Math.PI;
        break;
      case 'hunter':
        e.timer2 = rng.range(0, 4);
        e.variant = rng.int(0, 2);
        break;
      case 'seeker':
        e.rate = rng.range(0.8, 1.3);
        break;
      case 'dreadnought':
        e.size = 1;
        e.facing = -1;
        break;
    }

    // The hit radius follows the rendered size, once, here — so shooting an
    // animal and bumping into it agree about how big it is.
    e.radius = ENEMY[e.type].radius * e.size;

    e.anchored = anchor || e.type === 'turret' || e.type === 'mine';
    if (anchor || e.type === 'turret') {
      // Bolt it to the nearest rock face so it reads as installed, not floating.
      const n = { x: 0, y: 1 };
      let bestD = Infinity, bx = e.x, by = e.y;
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2;
        for (let t = 2; t < 26; t += 2) {
          const sx = e.x + Math.cos(a) * t;
          const sy = e.y + Math.sin(a) * t;
          if (density(sx, sy) > 0 && t < bestD) { bestD = t; bx = sx; by = sy; break; }
        }
      }
      if (bestD < Infinity) {
        gradient(bx, by, n);
        e.mountX = n.x;
        e.mountY = n.y;
        e.x = bx + n.x * (e.radius * 0.7);
        e.y = by + n.y * (e.radius * 0.7);
        e.homeX = e.x;
        e.homeY = e.y;
        e.aim = Math.atan2(n.y, n.x);
      }
    }
  }

  /**
   * Zones whose decorative medusae were handed to the actor system.
   *
   * See `jellyDrift`. These are the blue ones — a live jelly's colour is fixed
   * in the draw, so only a blue bloom can change hands without the zone losing
   * its palette.
   */
  private static readonly MEDUSA = new Set([
    'KELP FOREST', 'SUNKEN CITY', 'MILITARY ZONE', 'CAPITAL BERTH',
    'ANCIENT STRUCTURE',
  ]);

  /** Medusae drift through open water for the length of the level. */
  private spawnJellies(rng: Rng) {
    const amb = this.content.ambient;
    if (!amb) return;
    // Every lane gets its own drift of medusae. They are the thing that makes
    // water look inhabited rather than empty, and a lane with none reads as a
    // backdrop the moment you fly into it.
    for (let lane = 0; lane < LAYERS.length; lane++) {
      const L = LAYERS[lane]!;
      this.jellyDrift(rng, amb, lane, L);
    }
  }

  private jellyDrift(
    rng: Rng, amb: { x0: number; x1: number }, lane: number, L: LayerSpec,
  ) {
    // TIGHTER IN THE WATER THE PLAYER IS ACTUALLY IN, AND TIGHTEST WHERE THE
    // DECORATION USED TO BE.
    //
    // One every 55 to 135 m is a fair density for a lane you fly PAST. Lane 0
    // is the one you fly THROUGH, and until now its animals were outnumbered
    // four to one by scenery they could not shoot.
    //
    // MEDUSA_ZONES are the ones whose decorative blooms were BLUE. A live jelly
    // is blue — that colour is fixed in the draw — so those are the ones that
    // can become killable without the zone losing its palette. The Thermal
    // Vents keep theirs, because they are orange and nothing live can be; the
    // Abyss keeps its mixed tints for the same reason. Everywhere else the
    // props gave up most of their count and it arrives here instead, so what
    // the player sees drifting past is, mostly, something they can shoot.
    //
    // Computed in the update expression, not the body: every `continue` below
    // skips to it, and a step advanced inside the body would never run for a
    // rejected placement and hang the loop.
    const near = lane === 0;
    const step = (at: number) => {
      if (!near) return rng.range(55, 135);
      return Actors.MEDUSA.has(regionAt(at).name)
        ? rng.range(16, 38)
        : rng.range(24, 58);
    };
    for (let x = amb.x0; x < amb.x1; x += step(x)) {
      if (lane > 0 && !pocketAt(lane, x)) continue;
      // Drawn coordinates, seated on the ground of this lane.
      const floor = floorAt(x + L.warp) + layerLift(x, L);
      const y = floor + rng.range(10, 62);
      if (y > -6) continue;
      // They hang in open water; anything tight is terrain they would clip.
      const f = toField(lane, x, y, projField);
      if (!isClear(f.x, f.y, 7)) continue;
      const e = this.blank('jelly', x, y, rng);
      e.lane = lane;
      e.facing = 1;
      e.spin = 0;
      e.variant = rng.int(0, 4);
      e.size = rng.range(0.9, 2.4);
      e.radius = ENEMY.jelly.radius * e.size;
      // Stable for the life of the animal. Deriving it per frame from a
      // mutating field re-times the whole swim cycle every frame, which is
      // what made them look like they were convulsing.
      e.rate = rng.range(1.0, 1.6);
      this.enemies.push(e);
    }
  }

  /** Blooms hold deeper water than the drifting medusae. */
  private spawnBlooms(rng: Rng) {
    const amb = this.content.ambient;
    if (!amb) return;
    // Blooms start further out than jellies do, and that offset is theirs.
    //
    // Spacing pulled in from 150-340 m to 90-190. They are the best-looking
    // thing in the water and there was roughly one every quarter kilometre, so
    // most of a playthrough never saw two. A showpiece nobody meets is a
    // showpiece wasted.
    for (let lane = 0; lane < LAYERS.length; lane++) {
      const L = LAYERS[lane]!;
      this.bloomDrift(rng, amb, lane, L);
    }
  }

  private bloomDrift(
    rng: Rng, amb: { x0: number; x1: number }, lane: number, L: LayerSpec,
  ) {
    // Same argument as the medusae above, gentler: a bloom is a showpiece and
    // wants space around it, but not 190 m of it in the lane you are flying.
    const near = lane === 0;
    for (let x = amb.x0 + 260; x < amb.x1; x += rng.range(near ? 66 : 90, near ? 140 : 190)) {
      if (lane > 0 && !pocketAt(lane, x)) continue;
      const floor = floorAt(x + L.warp) + layerLift(x, L);
      const y = floor + rng.range(14, 54);
      if (y > -30) continue;
      const f = toField(lane, x, y, projField);
      if (!isClear(f.x, f.y, 13)) continue;
      const e = this.blank('bloom', x, y, rng);
      e.lane = lane;
      e.facing = 1;
      e.variant = rng.int(0, 2);
      // Big enough that the contour banding is actually visible; this is a
      // showpiece animal, not background life.
      e.size = rng.range(3.2, 6.4);
      e.radius = ENEMY.bloom.radius * e.size;
      e.rate = rng.range(0.75, 1.15);
      this.enemies.push(e);
    }
  }

  // ==========================================================================
  // firing
  // ==========================================================================

  private freeProjectile(): Projectile | null {
    for (const p of this.projectiles) if (!p.alive) return p;
    return null;
  }

  fireBullet(
    x: number, y: number, angle: number, from: Faction, speed: number, damage: number,
    lane = 0, baseZ = 0,
  ) {
    const p = this.freeProjectile();
    if (!p) return;
    p.alive = true;
    p.kind = 'bullet';
    p.from = from;
    p.x = x; p.y = y; p.z = baseZ; p.lane = lane;
    p.vx = Math.cos(angle) * speed;
    p.vy = Math.sin(angle) * speed;
    p.angle = angle;
    p.life = WEAPONS.cannon.life;
    p.damage = damage;
    p.radius = WEAPONS.cannon.radius;
    p.blast = 0;
    p.target = null;
    p.trail = 0;
    p.carve = 0;
    // Only the player's rounds skip. An enemy volley that ricocheted would
    // arrive from directions the player cannot read or plan around, which is
    // the difference between a weapon that rewards a clever angle and a room
    // that shoots you from behind.
    p.bounces = from === 'player' ? WEAPONS.cannon.bounces : 0;
    p.turnRate = 0;
    p.maxSpeed = speed;
    p.accel = 0;
  }

  fireTorpedo(
    x: number, y: number, angle: number, from: Faction, target: Trackable | null,
    opts: {
      damage?: number; blast?: number; speed?: number; turnRate?: number;
      life?: number; carve?: number;
      /** Draw-only lateral offset, for a tube that is not on the centreline. */
      z?: number;
      /** Lane to live in, and the depth of that lane. */
      lane?: number; baseZ?: number;
    } = {},
  ) {
    const p = this.freeProjectile();
    if (!p) return;
    const T = WEAPONS.torpedo;
    p.alive = true;
    p.kind = 'torpedo';
    p.from = from;
    p.x = x; p.y = y; p.z = (opts.baseZ ?? 0) + (opts.z ?? 0);
    p.lane = opts.lane ?? 0;
    const speed = opts.speed ?? T.speed;
    p.vx = Math.cos(angle) * speed;
    p.vy = Math.sin(angle) * speed;
    p.angle = angle;
    p.life = opts.life ?? T.life;
    p.damage = opts.damage ?? T.damage;
    p.radius = T.radius;
    p.blast = opts.blast ?? T.blastRadius;
    p.target = target;
    p.trail = 0;
    p.carve = opts.carve ?? (from === 'player' ? T.carve : 0);
    p.turnRate = opts.turnRate ?? T.turnRate;
    p.maxSpeed = Math.max(speed, T.maxSpeed);
    p.accel = T.accel;
  }

  /** Depth charge: no drive, sinks under its own weight, goes off on contact. */
  dropCharge(x: number, y: number, vx: number, vy: number, lane = 0, baseZ = 0) {
    const p = this.freeProjectile();
    if (!p) return;
    const C = WEAPONS.charge;
    p.alive = true;
    p.kind = 'charge';
    p.from = 'player';
    p.x = x; p.y = y; p.z = baseZ; p.lane = lane;
    p.vx = vx * 0.35;
    p.vy = vy * 0.35 - 3;
    p.angle = -Math.PI / 2;
    p.life = C.life;
    p.damage = C.damage;
    p.radius = C.radius;
    p.blast = C.blastRadius;
    p.target = null;
    p.trail = 0;
    p.carve = C.carve;
    p.turnRate = 0;
    p.maxSpeed = C.sink;
    p.accel = 0;
  }

  // ==========================================================================
  // update
  // ==========================================================================

  update(dt: number, ctx: ActorCtx) {
    this.time += dt;
    this.blasts.length = 0;
    this.updateProjectiles(dt, ctx);
    this.updateEnemies(dt, ctx);
    this.updatePickups(dt, ctx);
    this.updateStations(dt, ctx);
    this.resolveBlasts(ctx);
  }

  private updateProjectiles(dt: number, ctx: ActorCtx) {
    for (const p of this.projectiles) {
      if (!p.alive) continue;
      p.life -= dt;
      if (p.life <= 0) {
        p.alive = false;
        if (p.blast > 0) this.detonate(p, ctx);
        continue;
      }

      if (p.kind === 'torpedo') {
        // Accelerate, and steer toward a locked target (PRD §13).
        const speed = hyp(p.vx, p.vy);
        if (p.target && p.target.alive && p.turnRate > 0) {
          const want = Math.atan2(p.target.y - p.y, p.target.x - p.x);
          const d = angleDelta(p.angle, want);
          p.angle += clamp(d, -p.turnRate * dt, p.turnRate * dt);
        }
        const ns = Math.min(p.maxSpeed, speed + p.accel * dt);
        p.vx = Math.cos(p.angle) * ns;
        p.vy = Math.sin(p.angle) * ns;

        p.trail -= dt;
        if (p.trail <= 0) {
          p.trail = 0.018;
          ctx.onBubbleTrail(p.x, p.y);
        }
      } else if (p.kind === 'charge') {
        // Sinks, with a little drag so it settles rather than accelerating away.
        p.vy = Math.max(-p.maxSpeed, p.vy - 26 * dt);
        p.vx -= p.vx * 1.1 * dt;
        p.angle = Math.atan2(p.vy, p.vx);
        p.trail -= dt;
        if (p.trail <= 0) {
          p.trail = 0.06;
          ctx.onBubbleTrail(p.x, p.y);
        }
      }

      const steps = Math.max(1, Math.ceil((hyp(p.vx, p.vy) * dt) / 1.4));
      const sdt = dt / steps;
      for (let s = 0; s < steps && p.alive; s++) {
        p.x += p.vx * sdt;
        p.y += p.vy * sdt;

        // Terrain, in whatever lane the shot is flying. Between lanes there is
        // nothing to hit, which is why a negative lane skips the test entirely
        // rather than falling back to lane 0's rock.
        const pf = p.lane >= 0 ? toField(p.lane, p.x, p.y, projField) : null;
        if (pf && density(pf.x, pf.y) > -p.radius) {
          // A SHALLOW HIT SKIPS OFF.
          //
          // Only shallow: `vn` is how much of the round's speed was going into
          // the rock, and a round that went in nearly square has spent itself
          // on the wall. Skipping THAT would read as rubber. It is the graze
          // along a canyon wall that ricochets, which is also the shot the
          // player set up on purpose.
          //
          // The normal comes from the same `gradient` the hull is pushed out
          // with, so a round and a boat agree about which way a wall faces.
          if (p.kind === 'bullet' && p.bounces > 0) {
            const gl = gradient(pf.x, pf.y, projNormal);
            const nx = projNormal.x, ny = projNormal.y;
            const sp = hyp(p.vx, p.vy) || 1;
            const vn = (p.vx * nx + p.vy * ny) / sp;
            if (gl > 0.05 && vn > -0.66) {
              const d2 = 2 * (p.vx * nx + p.vy * ny);
              p.vx = (p.vx - d2 * nx) * 0.86;
              p.vy = (p.vy - d2 * ny) * 0.86;
              p.angle = Math.atan2(p.vy, p.vx);
              // Out of the rock by more than it went in, or the next substep
              // of this same frame finds the same wall and eats the bounce.
              p.x += nx * (p.radius + 0.6);
              p.y += ny * (p.radius + 0.6);
              p.damage *= 0.62;
              p.bounces--;
              ctx.onSpark(p.x, p.y, nx, ny, 6);
              continue;
            }
          }
          p.alive = false;
          if (p.blast > 0) this.detonate(p, ctx);
          else ctx.onSpark(p.x, p.y, -p.vx, -p.vy, 8);
          break;
        }

        if (p.from === 'player') {
          for (const e of this.enemies) {
            if (!e.alive || e.lane !== p.lane) continue;
            // A BROADPHASE, AND IT IS ONE SUBTRACTION AND TWO COMPARES.
            //
            // This scans EVERY enemy in the level for every projectile in
            // flight, every frame — 1,508 slots against a live population near
            // 900 — and a Chrome profile put 312 ms of self time in this
            // function across 28 seconds, the largest single item in the whole
            // trace by a factor of two.
            //
            // A projectile can only touch something within the sum of the two
            // radii. The largest in the game is the dreadnought's 15, and a
            // shot's own is under 2, so anything more than 64 m away in x is
            // unreachable — by a margin of four times the worst case. Rejecting
            // on that before the squared distance is worth computing is exact:
            // every pair this skips would have failed the test below anyway.
            const dx = e.x - p.x;
            if (dx > 64 || dx < -64) continue;
            const dy = e.y - p.y;
            const hitR = e.radius + p.radius;
            if (dx * dx + dy * dy > hitR * hitR) continue;
            p.alive = false;
            if (p.blast > 0) this.detonate(p, ctx);
            else {
              this.hurt(e, p.damage, ctx, p.x, p.y);
              ctx.onSpark(p.x, p.y, -p.vx, -p.vy, 10);
            }
            break;
          }
        } else if (p.lane === ctx.playerLane) {
          const dx = ctx.px - p.x, dy = ctx.py - p.y;
          const hitR = ctx.playerRadius + p.radius;
          if (dx * dx + dy * dy < hitR * hitR) {
            p.alive = false;
            if (p.blast > 0) this.detonate(p, ctx);
            else {
              ctx.hitPlayer(p.damage);
              ctx.onSpark(p.x, p.y, -p.vx, -p.vy, 10);
            }
            break;
          }
        }
      }
    }
  }

  private detonate(p: Projectile, ctx: ActorCtx) {
    const lane = Math.max(0, p.lane);
    this.blasts.push({
      x: p.x, y: p.y, lane, radius: p.blast, damage: p.damage, from: p.from,
      scale: p.blast / 11,
    });
    ctx.onExplosion(p.x, p.y, p.blast / 11);
    if (p.carve > 0) {
      // Carve where the rock IS, not where it is drawn. On lane 2 the two are
      // 1907.3 m apart, so this punched its crater most of two kilometres away
      // and the stone in front of the player was untouched — a torpedo that
      // hit a wall and did nothing to it.
      const f = toField(lane, p.x, p.y, projField);
      const span = carve(f.x, f.y, p.carve);
      // The span comes back in field x; the chunks to rebuild are addressed in
      // drawn x, so the warp has to come back off.
      if (span) {
        const w = LANE_WARP(lane);
        ctx.onCarve(span.x0 - w, span.x1 - w);
      }
    }
  }

  /**
   * Damage an enemy. `hx`/`hy` are where the hit landed, which is how armour
   * facing is resolved — a sentinel's plate only counts if the shot came into
   * the arc it is actually pointing at.
   */
  hurt(
    e: Enemy, amount: number, ctx: ActorCtx, hx?: number, hy?: number,
    /** True when this came from another enemy's blast — see `kill`. */
    noChain = false,
  ) {
    let dealt = amount;
    if (e.type === 'sentinel' && hx !== undefined && hy !== undefined) {
      dealt *= AI.sentinelSoak(e, hx, hy);
      if (dealt < amount * 0.5) ctx.onSpark(hx, hy, e.x - hx, e.y - hy, 6);
    }
    if (e.boss && hx !== undefined && hy !== undefined) {
      dealt = AI.bossHit(e, dealt, hx, hy);
    }

    e.hp -= dealt;
    e.hitFlash = 1;
    e.alert = 1;
    e.lastSeenX = ctx.px;
    e.lastSeenY = ctx.py;
    if (e.state === 'idle' || e.state === 'suspicious') this.setState(e, 'investigate');
    if (e.hp <= 0) this.kill(e, ctx, noChain);
  }

  kill(
    e: Enemy, ctx: ActorCtx,
    /**
     * Suppress this death's own blast.
     *
     * Set when the thing was killed BY an enemy blast, which caps a chain at
     * one link. Lowering the damage alone is not enough on its own: `blasts` is
     * appended to while `resolveBlasts` is iterating it, so a cascade can run
     * arbitrarily deep inside a single frame. This is what bounds it.
     */
    noChain = false,
  ) {
    if (!e.alive) return;
    e.alive = false;
    this.kills++;
    ctx.onScore(e.score);
    ctx.onKill(e);
    this.dropLoot(e);

    if (e.type === 'jelly' || e.type === 'bloom' || e.type === 'swarmer') {
      // Tissue, not ordnance: it comes apart in a glowing cloud.
      //
      // This used to be `onSpark` — the same orange effect a bullet makes on
      // rock — for an animal whose entire visual identity is that it glows. A
      // bloom got sixty of them and a jelly twenty-six, and killing either
      // looked like nothing had happened. They burst in their OWN colour now,
      // scaled by how big the animal was.
      if (e.type === 'swarmer') {
        ctx.onSpark(e.x, e.y, 0, 1, 14);
      } else if (e.type === 'bloom') {
        // The showpiece. Warm and iridescent, and worth the frame it costs.
        ctx.onBioBurst(e.x, e.y, Math.min(2.2, 0.5 + e.size * 0.26), 1.0, 0.55, 0.78);
      } else {
        ctx.onBioBurst(e.x, e.y, Math.min(1.6, 0.4 + e.size * 0.3), 0.42, 0.82, 1.0);
      }
      return;
    }
    if (e.type === 'lurker') {
      ctx.onSpark(e.x, e.y, 0, 1, 48);
      ctx.onExplosion(e.x, e.y, 0.6);
      return;
    }

    const scale = e.type === 'dreadnought' ? 5.5
      : e.type === 'mine' || e.type === 'seeker' ? 1.5
      : e.type === 'hunter' || e.type === 'sentinel' ? 2.1
      : e.type === 'turret' ? 1.7
      : 1.1;
    ctx.onExplosion(e.x, e.y, scale);
    ctx.onShake(Math.min(0.9, scale * 0.16));

    if ((e.type === 'mine' || e.type === 'seeker') && !noChain) {
      this.blasts.push({
        x: e.x, y: e.y, lane: e.lane,
        radius: MINE.blastRadius, damage: MINE.damage, from: 'enemy', scale,
      });
    }
    // Big hulls take rock with them.
    if (e.type === 'dreadnought' || e.type === 'sentinel') {
      const f = toField(Math.max(0, e.lane), e.x, e.y, projField);
      const span = carve(f.x, f.y, e.type === 'dreadnought' ? 20 : 9);
      if (span) {
        const w = LANE_WARP(e.lane);
        ctx.onCarve(span.x0 - w, span.x1 - w);
      }
    }
  }

  /** Scatter whatever the enemy was carrying. */
  private dropLoot(e: Enemy) {
    const table = DROPS[e.type];
    if (!table) return;
    for (const d of table.concat(this.extraDrops)) {
      if (this.suppressSalvage && d.type === 'salvage') continue;
      if (Math.random() > d.chance) continue;
      const a = Math.random() * Math.PI * 2;
      const s = LOOT.burst * (0.4 + Math.random() * 0.9);
      this.spawnLoot(
        e.x + Math.cos(a) * e.radius * 0.6,
        e.y + Math.sin(a) * e.radius * 0.6,
        d.type,
        d.amount,
        Math.cos(a) * s,
        Math.sin(a) * s + 2,
        // Loot belongs to the water the kill happened in, or it falls through
        // the floor it was dropped on and lands somewhere unreachable.
        { lane: e.lane },
      );
    }
  }

  /**
   * `opts.mass` turns this into CARGO rather than loot: it sinks, it settles,
   * it cannot be pocketed, and the only way it moves is on the winch. Expedition
   * needs that at runtime, because a contract is a crate placed on demand — the
   * campaign's crates are all authored, so nothing had ever asked before.
   */
  spawnLoot(
    x: number, y: number, type: PickupType, amount?: number, vx = 0, vy = 0,
    opts: { mass?: number; id?: string; label?: string; lane?: number;
            ore?: number } = {},
  ) {
    const def = PICKUP_DEF[type];
    // Reuse a faded slot before growing the array; a long fight in one arena
    // would otherwise leave hundreds of dead entries to iterate.
    let slot = this.pickups.find((p) => !p.alive && p.id === '');
    if (!slot) {
      slot = {
        alive: false, type, lane: 0, x: 0, y: 0, vx: 0, vy: 0, amount: 0, label: '',
        phase: 0, mass: 0, hold: 'free', homeX: 0, homeY: 0,
        revealed: true, id: '', life: 0, born: 0,
      };
      this.pickups.push(slot);
    }
    slot.alive = true;
    slot.lane = Math.max(0, opts.lane ?? 0);
    slot.type = type;
    slot.x = x; slot.y = y;
    slot.vx = vx; slot.vy = vy;
    slot.amount = amount ?? def.amount;
    slot.label = opts.label ?? def.label;
    // Cleared on a recycled slot, or a reused pickup would still be carrying
    // the last shard's seam and pay out the wrong mineral.
    slot.ore = opts.ore;
    slot.phase = Math.random() * 6.28;
    // Dropped loot is never cargo, and a recycled slot must not inherit weight.
    slot.mass = opts.mass ?? 0;
    slot.hold = 'free';
    slot.revealed = true;
    slot.id = opts.id ?? '';
    if (opts.label) slot.label = opts.label;
    // Cargo does not rot. A contract crate on a 40-second timer would vanish on
    // the way home, which is the one thing a haul must never do.
    slot.life = opts.mass ? Infinity : LOOT.life;
    slot.born = 0;
    return slot;
  }

  /**
   * Put one enemy in the world at a position.
   *
   * Lifted verbatim out of the authored zone loop so a wave director can reach
   * the same code path — a runtime spawn and an authored spawn have to produce
   * exactly the same enemy, or Survival slowly becomes a second game with its
   * own subtly different creatures.
   *
   * It ALWAYS pushes and never recycles a dead slot. `swarmGroups` holds Enemy
   * object references that `behaviors.ts` reads every frame, and the boss
   * restore and save-suppression loops walk `enemies` by identity and by id — a
   * reused slot leaves a ghost in a flock, or a non-swarmer inside one.
   * Reclaiming space is `compact()`'s job, between waves and never mid-frame.
   *
   * Returns null when the spot is solid rock after the de-embedding walk, which
   * a director must handle rather than assume away.
   */
  spawnEnemy(
    type: EnemyType, x: number, y0: number, rng: Rng,
    opts: {
      tough?: number; group?: number; id?: string; boss?: boolean; anchor?: boolean;
      march?: boolean; carrier?: PickupType; lane?: number;
    } = {},
  ): Enemy | null {
    const lane = Math.max(0, opts.lane ?? 0);
    let y = y0;
    // Never spawn inside rock — the rock of ITS OWN LANE. `x`/`y` are drawn
    // coordinates throughout; only the field lookups move.
    const L = LAYERS[Math.min(lane, LAYERS.length - 1)]!;
    const feats = featuresFor(x + L.warp - 8, x + L.warp + 8);
    const solid = (yy: number) => {
      const f = toField(lane, x, yy, projField);
      return density(f.x, f.y, feats);
    };
    let tries = 0;
    while (solid(y) > -4 && tries++ < 24) y += 3;
    if (solid(y) > -2) return null;

    const e = this.blank(type, x, y, rng);
    e.lane = lane;
    if (opts.tough) {
      e.maxHp = Math.round(e.maxHp * opts.tough);
      e.hp = e.maxHp;
      e.score = Math.round(e.score * opts.tough);
      e.salvage = Math.round(e.salvage * opts.tough);
    }
    if (opts.march) e.march = true;
    if (opts.carrier) e.carrier = opts.carrier;
    if (opts.group) e.group = opts.group;
    if (opts.id) e.id = opts.id;
    if (opts.boss) {
      e.boss = true;
      e.podMax = e.maxHp * 0.16;
      e.podA = e.podMax;
      e.podB = e.podMax;
    }
    this.configure(e, rng, opts.anchor === true);
    this.enemies.push(e);
    // A runtime swarmer has to join its flock, or it mills about alone while
    // the rest of its group takes turns lunging without it.
    if (e.type === 'swarmer') {
      let g = this.swarmGroups.get(e.group);
      if (!g) this.swarmGroups.set(e.group, (g = []));
      g.push(e);
    }
    return e;
  }

  /**
   * Reclaim dead enemies. Between waves ONLY — never mid-frame, and never in a
   * mode that has authored ids to protect.
   *
   * Anything carrying an id is a boss the save tracks by identity, so it stays
   * whatever its state. `swarmGroups` is rebuilt from scratch rather than
   * spliced, because it holds references into the array being rewritten.
   */
  compact(): number {
    const before = this.enemies.length;
    this.enemies = this.enemies.filter((e) => e.alive || e.id !== '' || e.boss);
    this.swarmGroups.clear();
    for (const e of this.enemies) {
      if (e.type !== 'swarmer') continue;
      let g = this.swarmGroups.get(e.group);
      if (!g) this.swarmGroups.set(e.group, (g = []));
      g.push(e);
    }
    return before - this.enemies.length;
  }

  private setState(e: Enemy, s: EnemyState) {
    e.state = s;
    e.stateTime = 0;
  }

  private updateEnemies(dt: number, ctx: ActorCtx) {
    // OUT-OF-LANE CREATURES USED TO TICK AT HALF RATE. THEY DO NOT NOW.
    //
    // The reasoning was sound and the implementation could not deliver it. A
    // creature the player cannot reach is doing one job — looking alive, which
    // is drift and a swim cycle — and neither reads differently at 30 Hz. But
    // the way it was done was `continue`, which skips the INTEGRATION as well
    // as the decision. Position was compensated with `edt = dt * 2`, so the
    // average speed was right and the motion was not: the creature stood
    // perfectly still for one frame and jumped twice as far the next.
    //
    // At 60 Hz that is a twitch, and then TAA makes it worse. The motion vector
    // is prev-to-current per frame, so it reads zero on the still frame and
    // double on the jump — the reprojection smears one and ghosts the other.
    // Convulsing is exactly the word.
    //
    // AND THE SAVING WAS NOT THERE. Measured on the machine reporting the
    // problem, with 832 enemies live:
    //
    //     CPU ms  upd 0.42  frame 0.02  draw 0.56  render 0.44  hud 0.51
    //     CPU total 1.95 of 16.65 ms frame
    //
    // The whole world update is 0.42 ms of a 16.65 ms budget. Halving the
    // off-lane half of it saves something like a tenth of a millisecond — 0.6
    // percent of a frame — and it was being paid for in the most visible
    // currency there is. This game is GPU-bound and thermally bound; it has
    // never been bound here.
    //
    // If it ever does need an LOD, the shape is to halve the DECISIONS and keep
    // integrating every frame, not to skip whole frames of movement. That means
    // splitting perception out of the behaviours, which is real work and should
    // wait until a measurement asks for it.
    for (const e of this.enemies) {
      if (!e.alive) continue;
      const dx = ctx.px - e.x, dy = ctx.py - e.y;
      /**
       * How far the player is, or INFINITY if it is in another lane.
       *
       * One value carries the whole of "cannot see, cannot reach, cannot be
       * provoked" into every species at once. Perception, the attack triggers,
       * the lurker's strike and the contact rule are all thresholds on this
       * distance, so an unreachable player simply never crosses any of them —
       * no new argument, no per-behaviour lane test, and nothing to forget in
       * the next creature. Two lanes apart the sub is 1907.3 m away through
       * rock; infinity is the honest number, not a fudge.
       */
      // Real separation, which is a different question from the one below.
      const near = hyp(dx, dy);
      const dist = e.lane === ctx.playerLane ? near : Infinity;
      e.hitFlash = damp(e.hitFlash, 0, 6, dt);

      // A lit fuse. Runs before the behaviour so a mine about to go off does
      // not also spend the frame steering, and kills with `noChain` false so
      // the cascade carries on — one hop per fuse rather than all of it now.
      if (e.fuse > 0) {
        e.fuse -= dt;
        e.alert = 1;
        if (e.fuse <= 0) { this.kill(e, ctx); continue; }
      }
      e.pinged = Math.max(0, e.pinged - dt / SONAR.contactLife);

      // Active sonar paints contacts — and reveals the player to them (PRD §15).
      if (ctx.sonarPing) {
        const pd = hyp(e.x - ctx.sonarPing.x, e.y - ctx.sonarPing.y);
        if (Math.abs(pd - ctx.sonarPing.r) < 12) {
          // The paint is allowed across lanes — a return off rock 400 m away
          // is exactly what sonar is for — but being GIVEN AWAY by it is not.
          e.pinged = 1;
          if (e.lane === ctx.playerLane && pd < SONAR.noiseRadius && AI.MECHANICAL.has(e.type)) {
            e.alert = Math.min(1, e.alert + 0.8);
            e.lastSeenX = ctx.px;
            e.lastSeenY = ctx.py;
            if (e.state === 'idle') this.setState(e, 'suspicious');
          }
        }
      }

      // The capital sub is enormous and slow; it stays live much further out so
      // it is already manoeuvring by the time it is on screen.
      // GATED ON REAL DISTANCE, NOT ON REACHABILITY.
      //
      // `dist` is Infinity in another lane, which is the right answer for
      // perception and the wrong one for this. It meant every creature outside
      // the player's lane failed this test forever and AI.update was never
      // called on it — so it never swam, never patrolled, never drifted. Frozen
      // where it spawned, wobbling only from the vertex animation, for the
      // whole life of the level. Switched off, and looking it.
      //
      // Two meanings had collapsed onto one number. Whether a creature can
      // reach the player is about lanes; whether it is worth simulating is
      // about metres. They are separated now, so the water in the next lane is
      // alive and still cannot touch you.
      //
      // Out of lane the radius is smaller, because out there the creature only
      // has to look alive rather than fight — and this used to be free, so it
      // is worth keeping the new cost bounded.
      const wake = (e.boss ? 620 : e.type === 'turret' ? 190 : 300)
        * (e.lane === ctx.playerLane ? 1 : 0.6);
      if (near > wake) continue;

      // Twice the step for the ones ticking half as often, or they would
      // simply move at half speed — which is the bug this looks like.
      // Was `dt * 2` for off-lane creatures, compensating a skipped frame
      // that no longer happens. Everything integrates at the real rate.
      const edt = dt;
      AI.update(this, e, edt, dx, dy, dist, ctx);
      e.stateTime += edt;

      // Shared rule: nothing that swims may END a frame inside rock.
      //
      // `avoid()` in behaviors.ts is a steering *force* that looks ahead along
      // velocity, so it cannot rescue anything already embedded — and the drag
      // in `seek` eats the nudge before it does much. An enemy that fled into a
      // wall or was blown into one simply stayed there, shooting out of solid
      // stone and unreachable without digging the rock away with torpedoes.
      //
      // Turrets and mines are exempt on purpose: they are installed in the
      // stone, and moving them off their mounts would be the bug, not the fix.
      if (!e.anchored) {
        const ef = toField(Math.max(0, e.lane), e.x, e.y, projField);
        const rf = featuresFor(ef.x - e.radius - 3, ef.x + e.radius + 3);
        const d = density(ef.x, ef.y, rf);
        if (d > -e.radius) {
          gradient(ef.x, ef.y, push, 0.55, rf);
          // Positional and scaled by depth, so a graze is a nudge and something
          // buried is ejected rather than creeping out over several seconds.
          const out = Math.min(e.radius * 2.2, d + e.radius + 0.4);
          e.x += push.x * out;
          e.y += push.y * out;
          // Cancel whatever is still driving it inward, or it swims straight
          // back in on the next frame and buzzes against the face.
          const into = e.vx * push.x + e.vy * push.y;
          if (into < 0) { e.vx -= push.x * into; e.vy -= push.y * into; }
        }
      }

      // Shared rule: nothing that swims may leave the water.
      //
      // The player has been clamped to `surfaceY - 2.2` since the beginning and
      // nothing else ever was, because the density field does not model the
      // surface as a ceiling — above the waterline it reports wide open space,
      // which to a fleeing or strafing enemy is the safest water on the map.
      // So they climbed out of the sea, went invisible above the surface plane,
      // and kept shooting from up there with nothing but a target bracket to
      // show for it. Unreachable, unkillable, and still dangerous.
      //
      // Clamped by the whole body rather than the centre, so nothing floats
      // half out; anchored species are exempt for the same reason as the rock
      // rule, being mounted rather than swimming.
      if (!e.anchored) {
        // The waterline of the lane it is in, and specifically the one that
        // lane is DRAWN to. `surfaceY + layerLift` was neither: the lift is
        // capped to keep a background seabed submerged, which over a deep
        // warped column puts the cap tens of metres UP, while the strip for
        // that same lane is independently clamped to `surfaceY - 2`. So the
        // rock stopped at the sea and the creatures did not — they hung in the
        // sky above it, jittering, because this clamp shoved them down every
        // frame and their own steering pushed them straight back up. See
        // `laneCeiling`, which is the single place both rules now live.
        const ceil = laneCeiling(e.x, LAYERS[Math.max(0, e.lane)]!) - 0.5 - e.radius;
        if (e.y > ceil) {
          e.y = ceil;
          if (e.vy > 0) e.vy = 0;
        }
      }

      // Shared contact rule, so no species has to remember to implement it.
      const s = ENEMY[e.type];
      if (s.contact > 0 && ctx.playerAlive && e.lane === ctx.playerLane) {
        const reach = ctx.playerRadius + e.radius;
        if (dist < reach) {
          if (ctx.ramming) {
            // Nitro turns the hull into the weapon. Anything short of a boss
            // gets shoved and shredded rather than trading damage.
            this.hurt(e, 120 * dt, ctx, ctx.px, ctx.py);
            const n = dist || 1;
            e.vx += (-dx / n) * 90 * dt;
            e.vy += (-dy / n) * 90 * dt;
            if (Math.random() < dt * 20) ctx.onSpark(e.x, e.y, -dx, -dy, 5);
          } else {
            ctx.grazePlayer(s.contact * dt);
            if (Math.random() < dt * 8) ctx.onSpark(e.x, e.y, 0, 1, 2);
          }
        }
      }
    }
  }

  private updatePickups(dt: number, ctx: ActorCtx) {
    for (const p of this.pickups) {
      if (!p.alive) continue;
      p.phase += dt;
      p.born = Math.min(1, p.born + dt * 3.2);

      if (p.life !== Infinity) {
        p.life -= dt;
        if (p.life <= 0) { p.alive = false; continue; }
      }

      if (!p.revealed && ctx.sonarPing) {
        const pd = hyp(p.x - ctx.sonarPing.x, p.y - ctx.sonarPing.y);
        if (pd < ctx.sonarPing.r) p.revealed = true;
      }

      const dx = ctx.px - p.x, dy = ctx.py - p.y;
      const dist = hyp(dx, dy);

      // A LOAD ON THE LINE STILL HAS WEIGHT.
      //
      // This used to `continue` outright, on the reasoning that the tow solver
      // owns the load and a second integrator would fight it. Half right: the
      // solver is a distance CONSTRAINT, and a constraint has no preferred
      // direction — so a crate on a slack line simply stayed wherever it was
      // last put. Stop the boat and the load hung out sideways at whatever
      // angle it happened to be at, weightless, which is what "the chain should
      // be hanging down" and "it doesn't gravitate towards the bottom" are both
      // describing.
      //
      // Gravity is integrated FIRST and the constraint corrects afterwards, in
      // that order, every frame — which is the standard way to get a pendulum
      // and is not a fight. The load now falls until the line goes taut, hangs
      // under the fairlead when the boat is still, and swings when it moves.
      if (p.hold === 'towed') {
        p.vy -= CARGO.sinkAccel * dt;
        p.vx -= p.vx * CARGO.towDrag * dt;
        p.vy -= p.vy * CARGO.towDrag * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        // Never let it hang inside the seabed while it is being carried.
        const tfp = toField(Math.max(0, p.lane), p.x, p.y, pickField);
        const tf = featuresFor(tfp.x - 3, tfp.x + 3);
        if (density(tfp.x, tfp.y, tf) > -CARGO.crateRadius) {
          const n = { x: 0, y: 1 };
          gradient(tfp.x, tfp.y, n, 0.55, tf);
          p.x += n.x * 8 * dt;
          p.y += n.y * 8 * dt;
          if (p.vy < 0) p.vy = 0;
        }
        continue;
      }

      // Loose loot drifts, then homes once the sub is close — the pull is what
      // makes a firefight's aftermath feel like a reward instead of a chore.
      if (p.id === '' && p.mass === 0) {
        // Sampled through the lane it fell in, integrated in drawn space — the
        // same split the sub's own collision uses. Without it a canister
        // dropped on lane 2 sinks through the floor it is drawn on and settles
        // on lane 0's rock, 54 m below anything the player can reach.
        const lf = toField(Math.max(0, p.lane), p.x, p.y, pickField);
        const feats = featuresFor(lf.x - 3, lf.x + 3);
        const d = density(lf.x, lf.y, feats);
        const range = POWER_MAGNET_RANGE * this.magnetScale;
        // Landed: near the rock and no longer moving. Same shape as cargo's
        // `settled`, and for the same reason — without it a piece of loot on
        // the floor fights the push-out at frame rate instead of lying there.
        const landed = d > -LOOT.restBand && hyp(p.vx, p.vy) < 1.2;

        if (dist < range && ctx.playerAlive && p.lane === ctx.playerLane) {
          // The magnet always wins, so a landed canister still comes when
          // called — otherwise the floor would collect a layer of loot that
          // could only be gathered by flying into each one.
          const k = 1 - dist / range;
          p.vx += (dx / (dist || 1)) * POWER_MAGNET_PULL * k * dt;
          p.vy += (dy / (dist || 1)) * POWER_MAGNET_PULL * k * dt;
        } else if (landed) {
          p.vx = 0;
          p.vy = 0;
        } else {
          p.vy -= LOOT.sink * dt;
        }
        p.vx -= p.vx * LOOT.drag * dt;
        p.vy -= p.vy * LOOT.drag * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;

        // Keep it out of the rock rather than letting it settle inside a wall.
        const lf2 = toField(Math.max(0, p.lane), p.x, p.y, pickField);
        if (density(lf2.x, lf2.y, feats) > -1.4) {
          const n = { x: 0, y: 1 };
          gradient(lf2.x, lf2.y, n, 0.55, feats);
          p.x += n.x * 6 * dt;
          p.y += n.y * 6 * dt;
          p.vx *= 0.6;
          p.vy *= 0.6;
        }
      } else if (p.mass > 0) {
        // Cargo, sitting in the water. It has weight, so it sinks rather than
        // hovering, and it comes to rest on the seabed where it was dropped —
        // a crate you cut loose is still there when you come back for it.
        const cf = toField(Math.max(0, p.lane), p.x, p.y, pickField);
        const feats = featuresFor(cf.x - 3, cf.x + 3);
        const d = density(cf.x, cf.y, feats);

        // Settled. The test is deliberately OUTSIDE the push-out branch and
        // uses a wider band than the branch does: a crate comes to rest just
        // *clear* of the surface that pushed it there, so a pin that only fires
        // while inside the push-out band almost never fires at all — the crate
        // sinks a centimetre, gets shoved back, and buzzes at frame rate
        // forever. `restBand` is the gap that stops the two fighting, and it is
        // also what keeps a crate lying normally on the floor out of
        // `solveTow`'s fouled branch.
        const settled = d > -(CARGO.crateRadius + CARGO.restBand)
          && hyp(p.vx, p.vy) < CARGO.restSpeed;

        if (settled) {
          p.vx = 0;
          p.vy = 0;
        } else {
          p.vy -= CARGO.crateSink * dt;
          p.vx -= p.vx * CARGO.crateDrag * dt;
          p.vy -= p.vy * CARGO.crateDrag * dt;
          p.x += p.vx * dt;
          p.y += p.vy * dt;
        }

        if (d > -CARGO.crateRadius) {
          const n = { x: 0, y: 1 };
          const cf2 = toField(Math.max(0, p.lane), p.x, p.y, pickField);
          gradient(cf2.x, cf2.y, n, 0.55, feats);
          p.x += n.x * 6 * dt;
          p.y += n.y * 6 * dt;
          p.vx *= 0.4;
          p.vy *= 0.4;
        }
      }

      // Cargo is never pocketed by flying into it. That is the entire point:
      // the only way it moves is on a line.
      if (
        p.revealed && p.mass === 0 && p.lane === ctx.playerLane
        && dist < LOOT.pickupRadius && ctx.playerAlive
      ) {
        p.alive = false;
        ctx.onPickup(p);
      }
    }
  }

  private updateStations(dt: number, ctx: ActorCtx) {
    for (const s of this.stations) {
      s.phase += dt;
      if (s.lane !== ctx.playerLane) { s.near = 0; continue; }
      const d = hyp(s.x - ctx.px, s.y - ctx.py);
      s.near = clamp(1 - d / STATION_RANGE, 0, 1);
    }
  }

  /** Nearest station the player is standing in, if any. */
  stationInRange(px: number, py: number): Station | null {
    let best: Station | null = null;
    let bestD = STATION_RANGE;
    for (const s of this.stations) {
      const d = hyp(s.x - px, s.y - py);
      if (d < bestD) { bestD = d; best = s; }
    }
    return best;
  }

  private resolveBlasts(ctx: ActorCtx) {
    for (const b of this.blasts) {
      for (const e of this.enemies) {
        // Same lane only: 431.7 m of rock is not a line of sight, however
        // close two things look on screen.
        if (!e.alive || e.lane !== b.lane) continue;
        const d = hyp(e.x - b.x, e.y - b.y);
        if (d > b.radius + e.radius) continue;
        const f = clamp(1 - d / (b.radius + e.radius), 0, 1);
        // An enemy blast barely scratches other enemies.
        //
        // 0.5 was tuned when a mine or two went off near each other. A seeker
        // detonates with a mine's 30 damage over a 15 m radius, and a seeker has
        // 22 hit points — so once seekers became the horde's mass, a front of
        // sixty was a self-clearing bomb. One went off, the neighbours died,
        // each of those pushed its own blast, and the wave deleted itself out at
        // the spawn line before the player ever saw it.
        const enemyBlast = b.from === 'enemy';
        // A CHAIN HAS TO RIPPLE, NOT TELEPORT.
        //
        // `blasts` is appended to while this loop is iterating it, so a kill
        // here pushes a blast that this same loop then resolves — the whole
        // cascade completes inside ONE frame. Measured: a single torpedo aimed
        // at x 971 detonated a mine at x 1086, 115 m away, on the same tick.
        // Every explosion in the field therefore lands simultaneously and
        // scattered, which a player reads as mines they already destroyed
        // exploding again, because nothing connects the flash to the shot.
        //
        // Ordnance caught in a blast gets a short fuse instead of dying now.
        // The chain still runs, one hop per fuse, and you can SEE it run —
        // which is the difference between a chain reaction and a bug.
        if (enemyBlast && (e.type === 'mine' || e.type === 'seeker') && e.fuse <= 0) {
          e.fuse = 0.14 + Math.random() * 0.12;
          e.hitFlash = 1;
          continue;
        }
        this.hurt(e, b.damage * f * (enemyBlast ? 0.15 : 1), ctx, b.x, b.y, enemyBlast);
        // Shove things around; a blast that only subtracts hit points reads flat.
        const n = d || 1;
        e.vx += ((e.x - b.x) / n) * 34 * f;
        e.vy += ((e.y - b.y) / n) * 34 * f;
      }
      const dp = hyp(ctx.px - b.x, ctx.py - b.y);
      // Same lane only. `playerLane` is -1 between lanes and a blast is always
      // clamped to a real one, so a sub crossing open water is untouchable —
      // which is correct: there is nothing out there to be caught by.
      if (b.lane === ctx.playerLane && dp < b.radius) {
        const f = 1 - dp / b.radius;
        // The player's own ordnance hurts far less than an enemy's — being
        // punished for using the fun weapon at close range is not fun.
        ctx.blastPlayer(b.damage * f * (b.from === 'player' ? 0.22 : 1));
      }
    }
  }

  /**
   * The upgraded sonar carries a concussive front (PRD §15): one radial thump
   * at the moment of the ping, not a travelling shell. Resolving it instantly
   * means an enemy can only be hit once per ping, and it makes the button read
   * as a panic move rather than a delayed maybe.
   */
  pulse(
    x: number, y: number, radius: number, damage: number, knock: number,
    ctx: ActorCtx, lane = 0,
  ) {
    for (const e of this.enemies) {
      if (!e.alive || e.lane !== lane) continue;
      const d = hyp(e.x - x, e.y - y);
      if (d > radius) continue;
      const f = clamp(1 - d / radius, 0.3, 1);
      this.hurt(e, damage * f, ctx, x, y);
      const n = d || 1;
      e.vx += ((e.x - x) / n) * knock * f;
      e.vy += ((e.y - y) / n) * knock * f;
    }
  }

  /** Enemies within the lock cone, nearest to the aim axis first (PRD §13). */
  lockCandidate(x: number, y: number, aim: number, lane = 0): Enemy | null {
    let best: Enemy | null = null;
    let bestScore = Infinity;
    for (const e of this.enemies) {
      if (!e.alive || e.lane !== lane || !ENEMY[e.type].lockable) continue;
      const dx = e.x - x, dy = e.y - y;
      const d = hyp(dx, dy);
      if (d > WEAPONS.lock.range) continue;
      const off = Math.abs(angleDelta(aim, Math.atan2(dy, dx)));
      if (off > WEAPONS.lock.cone) continue;
      const score = off * 90 + d * 0.35;
      if (score < bestScore) { bestScore = score; best = e; }
    }
    return best;
  }

  /** Live boss in the world, if one is awake. */
  activeBoss(): Enemy | null {
    for (const e of this.enemies) if (e.alive && e.boss && e.alert > 0.05) return e;
    return null;
  }
}

const POWER_MAGNET_RANGE = 26;
const POWER_MAGNET_PULL = 78;
const STATION_RANGE = 22;
